/**
 * SagsTidslinje — Visuel sagstidslinje med glassmorphism og Framer Motion
 * Erstatter den flade liste i KlientOversigt med en moderne 3D-tidslinje.
 *
 * Props:
 * haendelser — array af hændelser (allerede filtreret)
 * alle_haendelser — ufiltreret array til tæller
 * sag_søg / setSagSøg — søgetekst state
 * filter_type / setFilterType
 * filter_periode / setFilterPeriode
 * onRedigerHaendelse — callback({ id, titel, beskrivelse, type, begivenhedsdato, retning, part, haendelse_type, emne, haendelse_status })
 * onMarkerSet — callback(haendelse_id), kaldes når brugeren klikker en hændelse
 *   med h.er_ny === true (markerer den set for denne bruger via POST .../:id/set)
 * onFlytHaendelse — callback({ id, titel, filer_antal }) — flyt hændelse til anden sag
 * onDeadlineDialog — callback({ id, mode, deadline, titel })
 * onNotatOpdateret — v1.12 pkt 6: callback() uden argumenter, kaldes efter
 *   PATCH .../notat og POST .../notat/godkend er lykkedes — typisk
 *   parentens hent_haendelser() (refetch), så badge/notat-tekst opdateres.
 * sagId — bruges af HændelseFiler
 * HAENDELSE_TYPER_KO — typer fra App.js
 * beregn_deadline_info
 * HændelseFilTæller — component
 * HændelseFiler — component
 * T — tema-singleton
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from './api';
import { toast } from './utils';
// ── Farver og ikoner per type ──────────────────────────────
const TYPE_CFG = {
 note: { farve: '#60a5fa', bg: 'rgba(96,165,250,0.12)', ikon: '', label: 'Note' },
 aktindsigt: { farve: '#38bdf8', bg: 'rgba(56,189,248,0.12)', ikon: '', label: 'Aktindsigt' },
 mail: { farve: '#34d399', bg: 'rgba(52,211,153,0.12)', ikon: '', label: 'Mail' },
 mail_ind: { farve: '#34d399', bg: 'rgba(52,211,153,0.12)', ikon: '', label: 'Mail ind' },
 mail_ud: { farve: '#10b981', bg: 'rgba(16,185,129,0.12)', ikon: '', label: 'Mail ud' },
 opkald: { farve: '#f59e0b', bg: 'rgba(245,158,11,0.12)', ikon: '', label: 'Opkald' },
 // v1.12 pkt 6: AI-journaliserede Telavox-hændelser (services/telavox-
 // pipeline.js::journaliser_match sætter haendelser.type til præcis disse
 // to værdier) — faldt tidligere tilbage til den grå ukendte-type-styling.
 telefon: { farve: '#f59e0b', bg: 'rgba(245,158,11,0.12)', ikon: '', label: 'Telefonopkald' },
 telefonsvarer: { farve: '#fb923c', bg: 'rgba(251,146,60,0.12)', ikon: '', label: 'Telefonsvarer' },
 moede: { farve: '#a78bfa', bg: 'rgba(167,139,250,0.12)', ikon: '', label: 'Møde' },
 dokument: { farve: '#fb923c', bg: 'rgba(251,146,60,0.12)', ikon: '', label: 'Dokument' },
 myndighedsbrev: { farve: '#e879f9', bg: 'rgba(232,121,249,0.12)', ikon: '', label: 'Myndighedsbrev' },
 aftale: { farve: '#4ade80', bg: 'rgba(74,222,128,0.12)', ikon: '', label: 'Aftale' },
 faktura: { farve: '#fbbf24', bg: 'rgba(251,191,36,0.12)', ikon: '', label: 'Faktura' },
 deadline: { farve: '#f87171', bg: 'rgba(248,113,113,0.12)', ikon: '', label: 'Deadline' },
 deadline_udfoert: { farve: '#4ade80', bg: 'rgba(74,222,128,0.12)', ikon: '', label: 'Deadline udført' },
};
const cfg = (type) => TYPE_CFG[type] || { farve: '#94a3b8', bg: 'rgba(148,163,184,0.08)', ikon: '', label: type };
const HAENDELSE_TIL_DOKTYPE = { myndighedsbrev:'partshoring', aktindsigt:'redegoerelse', mail_ind:'partshoring', mail_ud:'brev_modpart', moede:'moedeoplaeg', opkald:'opsummering' };
const dok_knap_label = (type) => ['myndighedsbrev','aktindsigt','mail_ind'].includes(type) ? 'Skriv svar' : 'Generer dokument';
// ── v1.12 pkt 6 — [?]→tidsstempel-opslag ────────────────────────────────
// Spejler services/telavox-journalnotat.js's find_tidsstempel_for_ord (samme
// linjeformat "[m:ss] [Taler]: tekst") — duplikeret her, IKKE importeret, da
// frontend (CRA) og backend (Node) ikke deler kode andre steder i denne
// kodebase. Se backend-versionen for tests af selve opslags-logikken.
function escape_regex(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function parse_transskription_linjer(tekst) {
 return String(tekst || '').split(/\r?\n/).map(raa => {
  const m = raa.match(/^\[(\d{1,2}:\d{2})\]\s*\[[^\]]*\]:\s*(.*)$/);
  return m ? { tidsstempel: m[1], indhold: m[2] } : null;
 }).filter(Boolean);
}
function find_tidsstempel_for_ord(transskription_tekst, ord) {
 const linjer = parse_transskription_linjer(transskription_tekst);
 if (!linjer.length) return null;
 const ren_ord = String(ord || '').trim();
 if (ren_ord) {
  const re = new RegExp(`(?:^|[^\\p{L}\\p{N}])${escape_regex(ren_ord)}(?:[^\\p{L}\\p{N}]|$)`, 'iu');
  const match = linjer.find(l => re.test(l.indhold));
  if (match) return match.tidsstempel;
 }
 return linjer[0].tidsstempel;
}
function mmss_til_sekunder(mmss) {
 const m = String(mmss || '').match(/^(\d{1,2}):(\d{2})$/);
 return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}
// Finder lydfilen blandt hændelsens filer (journaliser_match uploader altid
// præcis 1 audio + 1 txt + 1 docx pr. AI-journaliseret hændelse).
function find_lyd_fil(filer) {
 return (filer || []).find(f => {
  const mime = (f.type || '').toLowerCase();
  const navn = (f.navn || '').toLowerCase();
  return mime.startsWith('audio') || /\.(mp3|wav|ogg|m4a|aac)$/.test(navn);
 }) || null;
}
// Deler notat-teksten op i tekst-segmenter og klikbare [?]-markører — hver
// markør husker det NÆRMESTE ord der stod umiddelbart før den (bruges til
// find_tidsstempel_for_ord ved klik). En linje-markør (intet ord lige før,
// fx en frisk linje der starter med "[?]") får ord=null — find_tidsstempel_
// for_ord falder da tilbage til transskriptionens første linje.
function split_paa_markoerer(tekst) {
 const dele = [];
 const regex = /\[\?\]/g;
 let sidste = 0, m, i = 0;
 while ((m = regex.exec(tekst)) !== null) {
  const foer = tekst.slice(sidste, m.index);
  dele.push({ type: 'tekst', tekst: foer, key: `t${i}` });
  const ord_match = foer.match(/([\p{L}\p{N}]+)\s*$/u);
  dele.push({ type: 'markoer', ord: ord_match ? ord_match[1] : null, key: `m${i}` });
  sidste = regex.lastIndex;
  i++;
 }
 dele.push({ type: 'tekst', tekst: tekst.slice(sidste), key: `t${i}` });
 return dele;
}
// ── NotatMedMarkoerer — renderer AI-notatet med klikbare [?]-markører ────
function NotatMedMarkoerer({ tekst, on_klik }) {
 const dele = React.useMemo(() => split_paa_markoerer(tekst || ''), [tekst]);
 return (
  <>
   {dele.map(d => d.type === 'tekst'
    ? <React.Fragment key={d.key}>{d.tekst}</React.Fragment>
    : (
     <button
      key={d.key}
      onClick={e => { e.stopPropagation(); on_klik(d.ord); }}
      title="Klik for at springe til dette sted i lydoptagelsen"
      style={{
       background: 'rgba(250,178,25,0.18)', border: '1px solid rgba(250,178,25,0.45)',
       borderRadius: 4, color: '#fab219', fontSize: '0.85em', fontWeight: 700,
       padding: '0 4px', margin: '0 1px', cursor: 'pointer', fontFamily: 'inherit',
       lineHeight: 1.4,
      }}
     >[?]</button>
    )
   )}
  </>
 );
}
// ── Grupér hændelser på dato ───────────────────────────────
function grupper_paa_dato(haendelser) {
 const grupper = {};
 for (const h of haendelser) {
 const dag = h.dato_raw
 ? new Date(h.dato_raw).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })
 : h.dato;
 if (!grupper[dag]) grupper[dag] = [];
 grupper[dag].push(h);
 }
 return Object.entries(grupper);
}
// ── Framer Motion varianter ────────────────────────────────
const kort_variants = {
 hidden: { opacity: 0, x: -20, y: 8 },
 visible: { opacity: 1, x: 0, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } },
 exit: { opacity: 0, x: 20, transition: { duration: 0.15 } },
};
const dato_variants = {
 hidden: { opacity: 0, scale: 0.85 },
 visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: 'easeOut' } },
};
// ── HændelsesKort ──────────────────────────────────────────
function HændelsesKort({ h, onRedigerHaendelse, onMarkerSet, onDeadlineDialog, onFlytHaendelse, beregn_deadline_info, HændelseFilTæller, HændelseFiler, T, onGenererDokument, onNotatOpdateret }) {
 const [åben, setÅben] = useState(false);
 const [hovered, setHovered] = useState(false);
 const c = cfg(h.type);
 const harIndhold = !!(h.beskrivelse || h.filer?.length > 0);
 // ── v1.12 pkt 6 — godkend-notat-flow ────────────────────────────────────
 const er_ai_notat = !!h.notat_status;
 const lyd_fil = er_ai_notat ? find_lyd_fil(h.filer) : null;
 const [transskription, setTransskription] = useState(null);
 const [rediger_notat, setRedigerNotat] = useState(false);
 const [notat_tekst, setNotatTekst] = useState('');
 const [notat_gemmer, setNotatGemmer] = useState(false);
 const [notat_godkender, setNotatGodkender] = useState(false);
 const antal_usikre = er_ai_notat ? (h.beskrivelse?.match(/\[\?\]/g) || []).length : 0;

 React.useEffect(() => {
  if (!åben || !er_ai_notat || !lyd_fil) return;
  let afbrudt = false;
  api(`filer/${lyd_fil.id}/transskription`).then(d => {
   if (!afbrudt && d && d.transskription) setTransskription(d.transskription);
  }).catch(() => {});
  return () => { afbrudt = true; };
  // lyd_fil er et nyt objekt hver render (find_lyd_fil kaldes uden memo) —
  // vi vil bevidst kun genhente ved skift af FIL-ID, ikke ved hver render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [åben, er_ai_notat, lyd_fil?.id]);

 const spring_til_tidsstempel = (ord) => {
  if (!lyd_fil) return;
  const tid = find_tidsstempel_for_ord(transskription, ord);
  if (!tid) return;
  const sek = mmss_til_sekunder(tid);
  const audio = document.getElementById(`audio-${lyd_fil.id}`);
  if (audio && sek != null) {
   audio.currentTime = sek;
   audio.play().catch(() => {});
   audio.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
 };

 const gem_notat = () => {
  setNotatGemmer(true);
  api(`haendelser/${h.id}/notat`, 'PATCH', { beskrivelse: notat_tekst })
   .then(() => { toast('Notat gemt', 'success'); setRedigerNotat(false); onNotatOpdateret && onNotatOpdateret(); })
   .catch(() => toast('Kunne ikke gemme notatet — prøv igen', 'fejl'))
   .finally(() => setNotatGemmer(false));
 };

 const godkend_notat = (e) => {
  e.stopPropagation();
  setNotatGodkender(true);
  api(`haendelser/${h.id}/notat/godkend`, 'POST')
   .then(() => { toast('Notat godkendt', 'success'); onNotatOpdateret && onNotatOpdateret(); })
   .catch(() => toast('Kunne ikke godkende notatet — prøv igen', 'fejl'))
   .finally(() => setNotatGodkender(false));
 };

 return (
 <motion.div
 id={`sv-haendelse-${h.id}`}
 variants={kort_variants}
 initial="hidden"
 animate="visible"
 exit="exit"
 layout
 onHoverStart={() => setHovered(true)}
 onHoverEnd={() => setHovered(false)}
 style={{
 position: 'relative',
 background: h.er_ny
 ? 'rgba(56,189,248,0.07)'
 : hovered
 ? `linear-gradient(135deg, ${c.bg}, rgba(255,255,255,0.04))`
 : 'rgba(255,255,255,0.03)',
 border: h.er_ny ? '1px solid rgba(56,189,248,0.4)' : `1px solid ${hovered ? c.farve + '55' : 'rgba(255,255,255,0.07)'}`,
 borderLeft: h.er_ny ? '3px solid #38bdf8' : `3px solid ${c.farve}`,
 borderRadius: 14,
 padding: '14px 16px',
 cursor: harIndhold ? 'pointer' : 'default',
 backdropFilter: 'blur(12px)',
 WebkitBackdropFilter: 'blur(12px)',
 boxShadow: h.er_ny
 ? '0 0 16px rgba(56,189,248,0.18), 0 2px 8px rgba(0,0,0,0.2)'
 : hovered
 ? `0 4px 20px ${c.farve}22, 0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)`
 : '0 2px 8px rgba(0,0,0,0.2)',
 transform: hovered ? 'translateY(-1px) translateX(2px)' : 'none',
 transition: 'all 0.22s cubic-bezier(0.34,1.56,0.64,1)',
 willChange: 'transform, box-shadow',
 }}
 onClick={() => {
 if (window.getSelection()?.toString()) return;
 if (h.er_ny) onMarkerSet && onMarkerSet(h.id);
 harIndhold && setÅben(v => !v);
 }}
 >
 {/* Glød-accent på hover */}
 {hovered && (
 <div style={{
 position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
 background: `linear-gradient(180deg, transparent, ${c.farve}, transparent)`,
 borderRadius: '3px 0 0 3px',
 boxShadow: `0 0 12px ${c.farve}`,
 }} />
 )}
 {/* Header */}
 <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
 {/* Type-ikon */}
 <div style={{
 width: 36, height: 36, borderRadius: 10,
 background: c.bg,
 border: `1px solid ${c.farve}44`,
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 fontSize: 17, flexShrink: 0,
 boxShadow: hovered ? `0 0 14px ${c.farve}44` : 'none',
 transition: 'box-shadow 0.2s',
 }}>
 {c.ikon}
 </div>
 {/* Titel + meta */}
 <div style={{ flex: 1, minWidth: 0 }}> <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}> <div style={{
 fontSize: 14, fontWeight: 600, color: T.creme, lineHeight: 1.35,
 whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
 userSelect: 'text', minWidth: 0,
 }}
 onClick={e => e.stopPropagation()}>
 {h.titel}
 </div>
 {h.er_ny && (
 <span title="Ny siden sidste besøg" style={{
 fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
 padding: '2px 8px', borderRadius: 20, flexShrink: 0,
 color: '#38bdf8', background: '#0a1420',
 border: '1px solid #38bdf8aa', boxShadow: '0 0 8px rgba(56,189,248,0.4)',
 }}>
 Ny
 </span>
 )}
 </div> <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}> <span style={{
 fontSize: 11, fontWeight: 500, color: c.farve,
 background: c.bg, borderRadius: 5, padding: '2px 7px',
 }}>
 {c.label}
 </span>
 {/* v1.12 pkt 6 — godkend-notat-badge */}
 {er_ai_notat && (h.notat_status === 'kladde' ? (
  <span style={{ fontSize: 11, fontWeight: 600, color: '#fab219', background: 'rgba(250,178,25,0.14)', border: '1px solid rgba(250,178,25,0.35)', borderRadius: 5, padding: '2px 7px' }}>
   KLADDE — kræver godkendelse
  </span>
 ) : (
  <span style={{ fontSize: 11, fontWeight: 600, color: '#4ade80', background: 'rgba(74,222,128,0.14)', border: '1px solid rgba(74,222,128,0.35)', borderRadius: 5, padding: '2px 7px' }}>
   Godkendt{h.notat_godkendt_af_navn ? ` af ${h.notat_godkendt_af_navn}` : ''}{h.notat_godkendt_kl ? ` · ${new Date(h.notat_godkendt_kl).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
  </span>
 ))}
 <span style={{ fontSize: 11, color: T.dæmpet }}>
 {h.dato}{h.tidspunkt ? ` kl. ${h.tidspunkt}` : ''}
 </span> <span style={{ fontSize: 11, color: T.subtil }}>· {h.forfatter}</span>
 <span title="Hændelsens id -- søg på #id i søgefeltet for at finde den igen" style={{ fontSize: 10.5, color: T.subtil, opacity: 0.65, fontFamily: "ui-monospace, 'JetBrains Mono', monospace" }}>#{h.id}</span>
 {h.deadline && (() => {
 const di = beregn_deadline_info(h.deadline);
 return (
 <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}> <span style={{
 fontSize: 11, color: di.farve,
 background: di.farve + '22', borderRadius: 5, padding: '2px 7px',
 cursor: 'pointer',
 }}
 onClick={e => { e.stopPropagation(); onDeadlineDialog({ id: h.id, sag_id: h.sag_id, mode: 'rediger', deadline: h.deadline?.split('T')[0] || '', titel: h.deadline_titel || '', original_titel: h.deadline_titel || '', årsag: '' }); }}
 title="Klik for at ændre deadline">
 {di.txt}
 </span> </span>
 );
 })()}
 {(h.type === 'mail_ind' || h.type === 'mail_ud' || h.type === 'mail') &&
   h.filer && h.filer.length > 0 && (
    <span
      title={`${h.filer.length} vedhæftning${h.filer.length !== 1 ? 'er' : ''}`}
      style={{
        fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 3,
        color: '#94a3b8', background: 'rgba(148,163,184,0.12)',
        borderRadius: 5, padding: '2px 6px',
      }}
    >
      📎 {h.filer.length}
    </span>
  )}
 {HændelseFilTæller && <HændelseFilTæller haendelse_id={h.id} />}
 </div>
 {/* Mail-preview: viser 3 linjer af beskrivelsen uden at klikke */}
 {!åben && h.beskrivelse && (h.type === 'mail_ind' || h.type === 'mail_ud' || h.type === 'mail') && (
  <div style={{
   fontSize: 12, color: T.dæmpet, lineHeight: 1.55,
   marginTop: 6, marginLeft: 48,
   whiteSpace: 'pre-wrap', wordBreak: 'break-word',
   overflow: 'hidden', maxHeight: '4.65em',
   maxWidth: '100%', userSelect: 'text', cursor: 'text',
  }}
  onClick={e => e.stopPropagation()}>
   {h.beskrivelse}
  </div>
 )}
 </div>
 {/* Handlingsknapper */}
 <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}> <button
 onClick={e => { e.stopPropagation(); onRedigerHaendelse({ id: h.id, titel: h.titel, beskrivelse: h.beskrivelse || '', type: h.type, begivenhedsdato: h.begivenhedsdato || h.dato || '', deadline: h.deadline?.split('T')[0] || '', deadline_titel: h.deadline_titel || '', retning: h.retning || null, part: h.part || null, haendelse_type: h.haendelse_type || null, emne: h.emne || null, haendelse_status: h.haendelse_status || null }); }}
 title="Rediger hændelse"
 style={{
 background: 'none', border: '1px solid rgba(255,255,255,0.1)',
 borderRadius: 6, color: T.dæmpet, cursor: 'pointer',
 fontSize: 11, padding: '4px 8px',
 transition: 'all 0.15s',
 }}
 onMouseOver={e => { e.currentTarget.style.color = T.creme; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
 onMouseOut={e => { e.currentTarget.style.color = T.dæmpet; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
 >Rediger</button>
 {onFlytHaendelse && (
 <button
 onClick={e => { e.stopPropagation(); onFlytHaendelse({ id: h.id, titel: h.titel, filer_antal: h.filer?.length || 0 }); }}
 title="Flyt til anden sag"
 style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: T.dæmpet, cursor: 'pointer', fontSize: 11, padding: '4px 8px', transition: 'all 0.15s' }}
 onMouseOver={e => { e.currentTarget.style.color = T.creme; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
 onMouseOut={e => { e.currentTarget.style.color = T.dæmpet; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
 >Flyt</button>
 )}
 {onGenererDokument && (
 <button
 onClick={e => { e.stopPropagation(); onGenererDokument({ haendelse_id: h.id, haendelse_titel: h.titel, dok_type: HAENDELSE_TIL_DOKTYPE[h.type] || '', forud_fil_ids: (h.filer || []).map(f => f.id), sbh_bemaerkning: 'Svar paa: ' + h.titel }); }}
 title={dok_knap_label(h.type)}
 style={{ background:'rgba(79,70,229,0.15)', border:'1px solid rgba(79,70,229,0.3)', borderRadius:6, color:'#a5b4fc', cursor:'pointer', fontSize:11, padding:'4px 8px', whiteSpace:'nowrap', flexShrink:0 }}
 onMouseOver={e => e.currentTarget.style.background='rgba(79,70,229,0.25)'}
 onMouseOut={e => e.currentTarget.style.background='rgba(79,70,229,0.15)'}
 >{dok_knap_label(h.type)}</button>
 )}
 {harIndhold && (
 <motion.div
 animate={{ rotate: åben ? 90 : 0 }}
 transition={{ duration: 0.2 }}
 style={{ display: 'flex', alignItems: 'center', padding: '4px 6px', color: T.dæmpet, fontSize: 11 }}
 >
 ▶
 </motion.div>
 )}
 </div> </div>
 {/* Indhold (kollapsibelt) */}
 <AnimatePresence>
 {åben && (
 <motion.div
 initial={{ height: 0, opacity: 0 }}
 animate={{ height: 'auto', opacity: 1 }}
 exit={{ height: 0, opacity: 0 }}
 transition={{ duration: 0.25, ease: 'easeInOut' }}
 style={{ overflow: 'hidden' }}
 >
 {h.beskrivelse && (
 <div style={{
 fontSize: 13, color: '#e2e8f0', lineHeight: 1.75,
 paddingLeft: 12, marginTop: 12, marginLeft: 47,
 whiteSpace: 'pre-wrap', wordBreak: 'break-word',
 borderLeft: `2px solid ${c.farve}33`,
 maxWidth: '100%', overflowX: 'auto',
 userSelect: 'text', cursor: 'text',
 }}
 onClick={e => e.stopPropagation()}>
 {er_ai_notat && rediger_notat ? (
  <div onClick={e => e.stopPropagation()}>
   <textarea
    value={notat_tekst}
    onChange={e => setNotatTekst(e.target.value)}
    style={{
     width: '100%', minHeight: 180, background: 'rgba(255,255,255,0.04)',
     border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: 10,
     color: T.creme, fontSize: 13, fontFamily: 'inherit', lineHeight: 1.6,
     boxSizing: 'border-box', resize: 'vertical',
    }}
   />
   <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
    <button
     disabled={notat_gemmer}
     onClick={gem_notat}
     style={{ fontSize: 12, fontWeight: 600, background: 'rgba(63,212,199,0.18)', border: '1px solid rgba(63,212,199,0.4)', borderRadius: 6, padding: '6px 14px', color: '#3fd4c7', cursor: notat_gemmer ? 'wait' : 'pointer' }}
    >{notat_gemmer ? 'Gemmer…' : 'Gem'}</button>
    <button
     onClick={() => setRedigerNotat(false)}
     style={{ fontSize: 12, background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '6px 14px', color: T.dæmpet, cursor: 'pointer' }}
    >Annuller</button>
   </div>
  </div>
 ) : er_ai_notat ? (
  <NotatMedMarkoerer tekst={h.beskrivelse} on_klik={spring_til_tidsstempel} />
 ) : h.beskrivelse}
 </div>
 )}
 {/* v1.12 pkt 6 — Ret/godkend-knapper for AI-journalnotater */}
 {er_ai_notat && !rediger_notat && h.notat_status !== 'godkendt' && (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10, marginLeft: 47 }} onClick={e => e.stopPropagation()}>
   {antal_usikre > 0 && (
    <span style={{ fontSize: 11, color: '#fab219' }}>
     ⚠ {antal_usikre} {antal_usikre === 1 ? 'usikkert sted' : 'usikre steder'}
    </span>
   )}
   <button
    onClick={() => { setNotatTekst(h.beskrivelse); setRedigerNotat(true); }}
    style={{ fontSize: 12, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 6, padding: '5px 12px', color: T.creme, cursor: 'pointer' }}
   >Ret notat</button>
   <button
    disabled={notat_godkender}
    onClick={godkend_notat}
    style={{ fontSize: 12, fontWeight: 600, background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.4)', borderRadius: 6, padding: '5px 12px', color: '#4ade80', cursor: notat_godkender ? 'wait' : 'pointer' }}
   >{notat_godkender ? 'Godkender…' : 'Godkend notat'}</button>
  </div>
 )}
 {HændelseFiler && <HændelseFiler haendelse_id={h.id} åben={true} />}
 </motion.div>
 )}
 </AnimatePresence> </motion.div>
 );
}
// ── Dato-separator ─────────────────────────────────────────
function DatoSeparator({ dato, antal, T }) {
 return (
 <motion.div
 variants={dato_variants}
 initial="hidden"
 animate="visible"
 style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 6px' }}
 > <div style={{
 fontSize: 11, fontWeight: 700, color: T.turkis,
 textTransform: 'uppercase', letterSpacing: '0.12em',
 background: 'rgba(13,148,136,0.12)', border: '1px solid rgba(13,148,136,0.25)',
 borderRadius: 8, padding: '3px 10px', whiteSpace: 'nowrap',
 }}>
 {dato}
 </div> <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(13,148,136,0.3), transparent)' }} /> <span style={{ fontSize: 10, color: T.subtil, whiteSpace: 'nowrap' }}>{antal} hændelse{antal !== 1 ? 'r' : ''}</span> </motion.div>
 );
}
// ── Filter-chip ────────────────────────────────────────────
function FilterChip({ aktiv, label, ikon, farve, onClick, antal }) {
 return (
 <motion.button
 whileHover={{ scale: 1.04, y: -1 }}
 whileTap={{ scale: 0.97 }}
 onClick={onClick}
 style={{
 background: aktiv ? (farve + '22') : 'rgba(255,255,255,0.04)',
 border: `1px solid ${aktiv ? farve : 'rgba(255,255,255,0.1)'}`,
 borderRadius: 20, padding: '5px 12px',
 color: aktiv ? farve : '#94a3b8',
 fontSize: 12, fontWeight: aktiv ? 600 : 400,
 cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
 transition: 'background 0.15s, border-color 0.15s, color 0.15s',
 fontFamily: "'Inter',system-ui,sans-serif",
 }}
 >
 {ikon && <span>{ikon}</span>}
 <span>{label}</span>
 {antal !== undefined && (
 <span style={{
 background: aktiv ? farve + '33' : 'rgba(255,255,255,0.08)',
 borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700,
 color: aktiv ? farve : '#64748b',
 minWidth: 18, textAlign: 'center',
 }}>
 {antal}
 </span>
 )}
 </motion.button>
 );
}
// ── Hoved-komponent ────────────────────────────────────────
export default function SagsTidslinje({
 haendelser = [],
 alle_haendelser = [],
 sag_søg = '',
 setSagSøg,
 filter_type = 'alle',
 setFilterType,
 filter_periode = 'alt',
 setFilterPeriode,
 onRedigerHaendelse,
 onMarkerSet,
 onDeadlineDialog,
 onFlytHaendelse,
 HAENDELSE_TYPER_KO = [],
 beregn_deadline_info,
 HændelseFilTæller,
 HændelseFiler,
 T,
 onGenererDokument,
 onNotatOpdateret,
 maal_id,
 onMaalVist,
}) {
 const søg_ref = useRef(null);
 const grupper = grupper_paa_dato(haendelser);
 const er_filtreret = sag_søg || filter_periode !== 'alt';

 // Scroll til + fremhæv en konkret hændelse (klik fra "Nye hændelser"-
 // widgeten i Overblik -- René: skal gå direkte til hændelsen, ikke blot
 // sagens overblik-fane). Forsøger indtil elementet findes, da hændelser
 // kan være under indlæsning når fanen lige er skiftet til.
 useEffect(() => {
  if (!maal_id) return;
  let forsøg = 0;
  // Bagdaterede hændelser sorteres efter begivenhedsdato, ikke
  // oprettet_dato -- en gammel hændelse kan derfor ligge langt nede i en
  // lang tidslinje (157+ hændelser set på Renés testsag). Den staggerede
  // kort-animation (staggerChildren: 0.04s pr. kort) betyder at et kort
  // langt nede først mountes flere sekunder inde -- et fast, kort
  // forsøgsvindue nåede derfor at give op FØR elementet overhovedet var i
  // DOM'en. Skalerer nu forsøgsvinduet med listens længde.
  const maks_forsøg = Math.max(20, Math.ceil((haendelser.length * 40 + 2000) / 150));
  const interval = setInterval(() => {
   forsøg++;
   const el = document.getElementById(`sv-haendelse-${maal_id}`);
   if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('sv-haendelse-fremhævet');
    setTimeout(() => el.classList.remove('sv-haendelse-fremhævet'), 2500);
    clearInterval(interval);
    onMaalVist && onMaalVist();
   } else if (forsøg > maks_forsøg) {
    clearInterval(interval); // hændelsen findes ikke (slettet/filtreret) -- giv op
   }
  }, 150);
  return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [maal_id, onMaalVist]);

 return (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
 {/* ── Søg + filter-bar ── */}
 <div style={{
 display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16,
 position: 'sticky', top: 0, zIndex: 10,
 backdropFilter: 'blur(12px)',
 WebkitBackdropFilter: 'blur(12px)',
 padding: '10px 0 8px',
 marginTop: -4,
 }}>
 {/* Søgefelt */}
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <div style={{ position: 'relative', flex: 1 }}> <span style={{
 position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
 fontSize: 14, color: T.dæmpet, pointerEvents: 'none', userSelect: 'none',
 }}></span> <input
 ref={søg_ref}
 value={sag_søg}
 onChange={e => setSagSøg(e.target.value)}
 placeholder="Søg i hændelser... (eller #id, fx #3554)"
 style={{
 width: '100%', boxSizing: 'border-box',
 background: 'rgba(255,255,255,0.06)',
 border: sag_søg ? '1px solid rgba(79,70,229,0.5)' : '1px solid rgba(255,255,255,0.12)',
 borderRadius: 8, padding: '8px 14px 8px 38px',
 color: T.creme, fontSize: 13,
 fontFamily: "'Inter',system-ui,sans-serif",
 outline: 'none',
 transition: 'border-color 0.2s, box-shadow 0.2s',
 boxShadow: sag_søg ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
 colorScheme: 'dark',
 }}
 onFocus={e => { e.target.style.borderColor = 'rgba(79,70,229,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.12)'; }}
 onBlur={e => { if (!sag_søg) { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; } }}
 />
 {sag_søg && (
 <button onClick={() => setSagSøg('')} style={{
 position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
 background: 'none', border: 'none', color: T.dæmpet, cursor: 'pointer', fontSize: 14, padding: '2px 4px',
 }}></button>
 )}
 </div>
 <span style={{ fontSize: 11, color: T.subtil, whiteSpace: 'nowrap' }}>
 {haendelser.length} af {alle_haendelser.length}
 </span>
 {er_filtreret && (
 <button onClick={() => { setSagSøg(''); setFilterType('alle'); setFilterPeriode('alt'); }}
 style={{
 background: 'none', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6,
 color: '#f87171', fontSize: 11, padding: '3px 8px', cursor: 'pointer', whiteSpace: 'nowrap',
 }}>
 Ryd
 </button>
 )}
 </div>
 </div>
 {/* ── Tomme tilstand ── */}
 {haendelser.length === 0 && (
 <motion.div
 initial={{ opacity: 0, y: 16 }}
 animate={{ opacity: 1, y: 0 }}
 style={{
 textAlign: 'center', padding: '48px 24px',
 background: 'rgba(255,255,255,0.02)',
 border: '1px dashed rgba(255,255,255,0.1)',
 borderRadius: 16,
 }}
 > <div style={{ fontSize: 36, marginBottom: 12 }}>
 {er_filtreret ? '' : ''}
 </div> <div style={{ fontSize: 14, fontWeight: 600, color: T.creme, marginBottom: 6 }}>
 {er_filtreret ? 'Ingen hændelser matcher filteret' : 'Ingen hændelser endnu'}
 </div> <div style={{ fontSize: 12, color: T.dæmpet }}>
 {er_filtreret ? 'Prøv et andet filter eller søgebegreb' : 'Opret den første hændelse med formularen nedenfor'}
 </div>
 {er_filtreret && (
 <button
 onClick={() => { setSagSøg(''); setFilterType('alle'); setFilterPeriode('alt'); }}
 style={{
 marginTop: 16, background: 'rgba(13,148,136,0.12)',
 border: '1px solid rgba(13,148,136,0.3)', borderRadius: 8,
 color: T.turkis, fontSize: 12, padding: '8px 16px', cursor: 'pointer',
 }}
 >
 Ryd alle filtre
 </button>
 )}
 </motion.div>
 )}
 {/* ── Tidslinje ── */}
 {haendelser.length > 0 && (
 <div style={{ position: 'relative' }}>
 {/* Vertikal linje */}
 <div style={{
 position: 'absolute',
 left: 17, top: 32, bottom: 20,
 width: 2,
 background: 'linear-gradient(180deg, rgba(13,148,136,0.6) 0%, rgba(79,70,229,0.3) 60%, transparent 100%)',
 borderRadius: 2,
 }} /> <motion.div
 initial="hidden"
 animate="visible"
 variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
 style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
 >
 {grupper.map(([dato, items]) => (
 <div key={dato} style={{ marginBottom: 12 }}>
 {/* Dato separator med tidslinje-dot */}
 <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 8 }}>
 {/* Stor dot på tidslinjen */}
 <div style={{
 width: 36, height: 36, flexShrink: 0,
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 }}> <div style={{
 width: 12, height: 12, borderRadius: '50%',
 background: 'linear-gradient(135deg, #0d9488, #4f46e5)',
 boxShadow: '0 0 10px rgba(13,148,136,0.5)',
 border: '2px solid rgba(13,148,136,0.3)',
 }} /> </div> <DatoSeparator dato={dato} antal={items.length} T={T} /> </div>
 {/* Hændelser for denne dato */}
 <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 36 }}> <AnimatePresence>
 {items.map(h => (
 <HændelsesKort
 key={h.id}
 h={h}
 onRedigerHaendelse={onRedigerHaendelse}
 onMarkerSet={onMarkerSet}
 onDeadlineDialog={onDeadlineDialog}
 onFlytHaendelse={onFlytHaendelse}
 beregn_deadline_info={beregn_deadline_info}
 HændelseFilTæller={HændelseFilTæller}
 HændelseFiler={HændelseFiler}
 T={T}
 onGenererDokument={onGenererDokument}
 onNotatOpdateret={onNotatOpdateret}
 />
 ))}
 </AnimatePresence> </div> </div>
 ))}
 </motion.div> </div>
 )}
 </div>
 );
}
